import { useState, useEffect } from "react";
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { User, Buildings, FlowArrow, Cpu } from "@phosphor-icons/react";
import axios from "axios";
import { API } from "@/App";

export default function SearchCommand({ open, onOpenChange, onSelect }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setResults([]);
    }
  }, [open]);

  useEffect(() => {
    if (query.length < 2) {
      setResults([]);
      return;
    }

    const searchTimeout = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/search`, {
          params: { q: query },
          withCredentials: true,
        });
        setResults(response.data.results || []);
      } catch (error) {
        console.error("Search error:", error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [query]);

  const getIcon = (type) => {
    switch (type) {
      case "role": return <User size={16} className="text-primary" />;
      case "department": return <Buildings size={16} className="text-green-600" />;
      case "process": return <FlowArrow size={16} className="text-violet-600" />;
      case "system": return <Cpu size={16} className="text-red-600" />;
      default: return null;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case "role": return "Role";
      case "department": return "Department";
      case "process": return "Process";
      case "system": return "System";
      default: return type;
    }
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Search roles, departments, processes, systems..."
        value={query}
        onValueChange={setQuery}
        data-testid="search-input"
      />
      <CommandList>
        {loading && (
          <div className="py-6 text-center text-sm text-muted-foreground">
            Searching...
          </div>
        )}
        {!loading && query.length >= 2 && results.length === 0 && (
          <CommandEmpty>No results found.</CommandEmpty>
        )}
        {!loading && results.length > 0 && (
          <CommandGroup heading="Results">
            {results.map((result) => (
              <CommandItem
                key={`${result.type}-${result.id}`}
                value={`${result.type}-${result.id}`}
                onSelect={() => onSelect(result)}
                className="flex items-center gap-3 cursor-pointer"
                data-testid={`search-result-${result.id}`}
              >
                {getIcon(result.type)}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{result.title}</p>
                  <p className="text-xs text-muted-foreground truncate">{result.subtitle}</p>
                </div>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground bg-secondary px-2 py-0.5 rounded">
                  {getTypeLabel(result.type)}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {!loading && query.length < 2 && (
          <div className="py-6 text-center text-sm text-muted-foreground">
            Type at least 2 characters to search
          </div>
        )}
      </CommandList>
    </CommandDialog>
  );
}
